import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './AnnexureEntry.css';
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExcelJS from 'exceljs';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { getDistrict, getProjects, getStates, getClients, getProject, fetchMonths, getAnnexureEntry } from '../../../services/monthlyAttendanceService';
import { capitalizeFirstLetter } from '../../../utils/validations';
const AnnexureEntry = () => {
    const [state, setState] = useState(null);
    const [district, setDistrict] = useState(null);
    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [stateOptions, setStateOptions] = useState([]);
    const [monthOptions, setMonthOptions] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [projectResponse, setProjectResponse] = useState([]);
    const [project, setProject] = useState(null);
    const [clientResponse, setClientResponse] = useState([]);
    const [clients, setClients] = useState(null);
    const effectRan = useRef(false);
    const [isFocused, setIsFocused] = useState({
        state: false,
        district: false,
        month: false,
        year: false,
        project: false,
        clients: false,
    });
    const [errors, setErrors] = useState({});
    const yearInputRef = useRef(null);
    const [hasFetched, setHasFetched] = useState(false); // New state to track fetch attempt
    const isFetchDataEnabled = state && month && year && project;
    const [designationSummary, setDesignationSummary] = useState([]);
    const [loading, setLoading] = useState(false);
    // Fetch states
    useEffect(() => {
        const fetchStates = async () => {
            try {
                // const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/states/nationality/1`);
                const data = await getStates();
                setStateOptions(
                    data.map(item => ({
                        value: item.id,
                        label: capitalizeFirstLetter(item.stateName),
                    }))
                );
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };
        if (!effectRan.current) {
            fetchStates();
        }
        return () => {
            effectRan.current = true;
        }
    }, []);

    // Fetch months when year changes
    useEffect(() => {
        const loadMonths = async () => {
            try {
                const months = await fetchMonths(year);
                setMonthOptions(months);
            } catch (error) {
                console.error("Error fetching months:", error);
                setErrors((prev) => ({ ...prev, general: "Failed to load months" }));
            }
        };
        loadMonths();
    }, [year]);


    // Fetch districts when state changes
    useEffect(() => {
        if (state) {
            const fetchClients = async () => {
                try {
                    // const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects`);
                    const data = await getClients(state);
                    setClientResponse(data.map(client => ({
                        value: client.id,
                        label: client.clientName,
                    }))
                    );
                } catch (error) {
                    console.error('Error fetching states:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
                }
            };
            fetchClients();
        }
        else {
            setClients(null);
        }
    }, [state]);


    useEffect(() => {
        if (clients) {
            const fetchProjects = async () => {
                try {
                    const data = await getProject(clients);
                    setProjectResponse(data.map(pro => ({
                        value: pro.id,
                        label: pro.projectCode,
                    })));
                } catch (error) {
                    console.error('Error fetching projects:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load projects' }));
                }
            };

            fetchProjects();
        } else {
            setProjectResponse([]); // Reset projects if no client is selected
        }
    }, [clients]);

    // Clear attendance data and fetch status when any dropdown changes
    useEffect(() => {
        setAttendanceData([]);
        // setOriginalData([]);
        setHasFetched(false);
    }, [state, district, month, year, project]); // ✅ Add project to depende

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                setMonth(null);
                setProject(null); // ✅ Clear project when state changes
                break;
            case 'clients':
                setClients(value);
                setProject(null);
                break;
            case 'month':
                setMonth(value);
                break;
            case 'project':
                setProject(value); // ✅ Handle project selection
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleYearChange = (date) => {
        setYear(date ? date.getFullYear() : null);
        setErrors(prev => ({ ...prev, year: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!state) newErrors.state = 'State is required';
        //if (!district) newErrors.district = 'District is required';
        if (!month) newErrors.month = 'Month is required';
        if (!year) newErrors.year = 'Year is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleFetch = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            setLoading(true); // ✅ Start loader before API call

            // 🔹 Fetch your existing API
            const data = await getAnnexureEntry({
                stateId: state,
                districtId: district,
                clientId: clients,
                month,
                year,
                projectId: project,
            });

            console.log("Fetched Data:", data);

            // ✅ Map short codes → full names
            const designationMap = {
                PM: "Project Manager",
                PMDC: "Data Center Project Manager",
                "SYSTEM ADMIN": "System Administrator",
                APM: "Assistant Project Manager",
                ITM: "IT Managers",
                CRO: "Control Room Operators",
                HDO: "Help Desk Operators",
                CCO: "Call Center Operators",
                DEO: "Godown Operators",
                HDOP: "Help Desk Operator ",
            };

            const designationOrder = [
                "PM",
                "PMDC",
                "SYSTEM ADMIN",
                "APM",
                "ITM",
                "CRO",
                "HDO",
                "CCO",
                "DEO",
                "HDOP"
            ];

            // ✅ Sort & rename your API data
            const sortedData = data
                .map((item) => ({
                    ...item,
                    fullName: designationMap[item.designation] || item.designation,
                }))
                .sort(
                    (a, b) =>
                        designationOrder.indexOf(a.designation) -
                        designationOrder.indexOf(b.designation)
                );

            setDesignationSummary(sortedData);
            console.log("Sorted Designation Summary:", sortedData);
        } catch (error) {
            console.error("Error fetching data:", error);
            setErrors((prev) => ({
                ...prev,
                general: "Failed to fetch report data",
            }));
            alert("Failed to fetch report data. Please try again.");
        } finally {
            setLoading(false); // ✅ Stop loader whether success or fail
        }
    };



    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log("Save button clicked ✅");
        const fullName = localStorage.getItem("full_name");
        const role = localStorage.getItem("role");
        const employeeId = localStorage.getItem("employeeId");
        // ✅ Mapping between short codes and backend IDs
        const designationIdMap = {
            PM: 8,
            PMDC: 47,
            "SYSTEM ADMIN": 46,
            APM: 32,
            ITM: 33,
            CRO: 34,
            HDO: 36,
            CCO: 35,
            DEO: 38,
            HDOP: 37,
        };
        try {
            // Construct payload as per your API requirement
            const payload = designationSummary
                .filter((item) => !(project === 2 && item.designation === "HDO"))

                .map((item) => ({
                    stateId: state,
                    clientId: clients,
                    projectId: project,
                    designationId: designationIdMap[item.designation],
                    year: year,
                    month: month,
                    noOfResourcesDeployed: item.employeeCount,
                    totalDaysPresent: item.totalPresentDays,
                    ratePerMonth: item.rate,
                    totalAmount: Math.round(item.totalAmount || 0), // ✅ Round to nearest integer
                    auditInfo: {
                        name: fullName,
                        role,
                        employeeId,
                    },
                }));

            console.log("Saving payload : ", payload);
            console.log("Designation : ", designationSummary);

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annexure-a/save`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                // ✅ Show backend message if exists
                const errorMessage = result?.error || "Failed to save data.";
                setErrors((prev) => ({ ...prev, general: errorMessage }));
                return;
            }

            const result = await response.json();
            console.log("Save successful:", result);
            alert("Data saved successfully!");

        } catch (error) {
            console.error("Error saving data:", error);
            alert("Annexure A already exists for the selected project and month.");

        }
    };


    const currentYear = new Date().getFullYear();
    const minDate = new Date(2000, 0, 1);
    const maxDate = new Date(currentYear, 11, 31);
    // Handle rate input and calculate total
    const handleRateChange = (e, index) => {
        const newRate = parseFloat(e.target.value) || 0;
        const daysInMonth = new Date(year, month, 0).getDate();

        setAttendanceData((prev) =>
            prev.map((row, i) => {
                if (i === index) {
                    const perDay = newRate / daysInMonth;
                    const total = perDay * row.totalPresentDays;
                    return { ...row, rate: newRate, totalAmount: total };
                }
                return row;
            })
        );
    };


    return (
        <div className="annexure-Entry-container">
            <div className="annexure-Entry-box">
                <h2 className="annexure-Entry-heading">ANNEXURE - A </h2>
                {errors.general && <div className="error-message">{errors.general}</div>}
                <form onSubmit={handleFetch}
                >
                    <div className="annexure-Entry-maincontainer">
                        <div className="annexure-Entry-container-part">
                            <div className={`annexure-Entry-floating-label-container ${isFocused.state || state ? 'float-label' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === state) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}

                                />
                                <label><span className="required">*</span>State</label>
                                {errors.state && <div className="error-message">{errors.state}</div>}
                            </div>
                        </div>
                        <div className="annexure-Entry-container-part">

                        </div>
                    </div>
                    <div className="annexure-Entry-maincontainer">
                        <div className="annexure-Entry-container-part">
                            <div className={`annexure-Entry-floating-label-container ${isFocused.year || year ? 'float-label' : ''}`}>
                                <DatePicker
                                    selected={year ? new Date(year, 0, 1) : null}
                                    onChange={handleYearChange}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, year: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, year: false }))}
                                    dateFormat="yyyy"
                                    showYearPicker
                                    placeholderText=""
                                    className="annexure-Entry-date-picker"
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    ref={yearInputRef}

                                    title="Select a year"
                                />
                                <label><span className="required">*</span>Year</label>
                                {errors.year && <div className="error-message">{errors.year}</div>}
                            </div>
                        </div>
                        <div className="annexure-Entry-container-part">
                            <div className={`annexure-Entry-floating-label-container ${isFocused.month || month ? 'float-label' : ''}`}>
                                <Select
                                    name="month"
                                    options={monthOptions}
                                    value={monthOptions.find(opt => opt.value === month) || null}
                                    onChange={option => handleInputChange(option, 'month')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, month: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, month: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>Month</label>
                                {errors.month && <div className="error-message">{errors.month}</div>}
                            </div>
                        </div>
                    </div>
                    <div className="annexure-Entry-maincontainer">
                        <div className="annexure-report-container-part">
                            <div className={`annexure-report-floating-label-container ${isFocused.clients || clients ? 'float-label' : ''}`}>
                                <Select
                                    name="project"
                                    options={clientResponse}
                                    value={clientResponse.find(opt => opt.value === clients) || null}
                                    onChange={option => handleInputChange(option, 'clients')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, clients: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, clients: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>Client</label>
                                {errors.project && <div className="error-message">{errors.project}</div>}
                            </div>

                        </div>
                        <div className="annexure-Entry-container-part">
                            <div className={`annexure-Entry-floating-label-container ${isFocused.project || project ? 'float-label' : ''}`}>
                                <Select
                                    name="project"
                                    options={projectResponse}
                                    value={projectResponse.find(opt => opt.value === project) || null}
                                    onChange={option => handleInputChange(option, 'project')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, project: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, project: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label>
                                    <span className="required">*</span>Project
                                </label>
                                {errors.project && <div className="error-message">{errors.project}</div>}
                            </div>
                        </div>

                    </div>
                    <div className="annexure-Entry-btn-div">
                        <button type="submit" className="annexure-Entry-btn"
                            disabled={!isFetchDataEnabled}


                        >
                            Fetch Data
                        </button>
                    </div>
                </form>
            </div>
            <div className="annexure-Entry-btn-div">
                <button
                    type="button"   // ✅ Prevents it from trying to submit the other form
                    className="annexure-Entry-btn"
                    onClick={handleSubmit}   // ✅ Correct event handler
                    disabled={designationSummary.length === 0}
                >
                    SAVE
                </button>
            </div>

            <div className="annexure-table">

                {loading && (
                    <div className="loading-message">
                        <p>Loading data, please wait...</p>
                    </div>
                )}
                {/* ✅ Show table only when data is fetched */}
                {!loading &&designationSummary.length > 0 && project === 1 && (
                    <div className="annexure-mid-table">
                        <table className="annexure-report">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Business Operations Charges</th>
                                    <th>No. of Resources Deployed</th>
                                    <th>Total Days Present</th>
                                    <th>Rate per Month (INR)</th>
                                    <th>Total Amount (INR)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {designationSummary.map((item, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{item.fullName}</td>
                                        <td>{item.employeeCount}</td>
                                        <td>{item.totalPresentDays}</td>
                                        <td>
                                            <input
                                                type="number"
                                                value={item.rate || ""}
                                                onChange={(e) => {
                                                    const rate = parseFloat(e.target.value) || 0;
                                                    const daysInMonth = new Date(year, month, 0).getDate(); // month is 1-based (e.g., 10 for October)
                                                    const perDay = rate / daysInMonth;
                                                    const total = perDay * item.totalPresentDays;

                                                    // Update the specific row in state
                                                    const updated = [...designationSummary];
                                                    updated[index] = { ...item, rate, totalAmount: total };
                                                    setDesignationSummary(updated);
                                                }}
                                                placeholder="Enter rate"
                                            />
                                        </td>
                                        <td>
                                            {item.totalAmount
                                                ? item.totalAmount
                                                    .toLocaleString("en-IN", { maximumFractionDigits: 0 })
                                                : "-"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>

                            {/* ✅ Footer calculations */}
                            <tfoot>
                                {(() => {
                                    const subTotal = designationSummary.reduce(
                                        (sum, item) => sum + (item.totalAmount || 0),
                                        0
                                    );
                                    const cgst = subTotal * 0.09;
                                    const sgst = subTotal * 0.09;
                                    const grandTotal = subTotal + cgst + sgst;

                                    return (
                                        <>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    Sub Total
                                                </th>
                                                <th>{subTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</th>


                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    CGST @ 9%
                                                </th>
                                                <th>{cgst.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>
                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    SGST @ 9%
                                                </th>
                                                <th>{sgst.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>

                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    TOTAL
                                                </th>
                                                <th>{grandTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</th>
                                            </tr>
                                        </>
                                    );
                                })()}
                            </tfoot>
                        </table>
                    </div>
                )}



                {/* ✅ ePDS Table — also show only after data fetch */}
                {!loading &&designationSummary.length > 0 && project === 2 && (
                    <div className='annexure-mid-table'>
                        <table className="annexure-report">

                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Business Operations Charges</th>
                                    <th>No. of Resources Deployed</th>
                                    <th>Total Days Present</th>
                                    <th>Rate per Month (INR)</th>
                                    <th>Total Amount (INR)</th>
                                </tr>
                            </thead>

                            <tbody>
                                {designationSummary
                                    .filter((item) => !(project === 2 && item.designation === "HDO"))
                                    .map((item, index) => (
                                        <tr key={item.designation}>
                                            <td>{index + 1}</td>
                                            <td>{item.fullName}</td>
                                            <td>{item.employeeCount}</td>
                                            <td>{item.totalPresentDays}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    value={item.rate || ""}
                                                    onChange={(e) => {
                                                        const rate = parseFloat(e.target.value) || 0;
                                                        const daysInMonth = new Date(year, month, 0).getDate(); // month is 1-based (e.g., 10 for October)
                                                        const perDay = rate / daysInMonth;
                                                        const total = perDay * item.totalPresentDays;

                                                        const updated = designationSummary.map((d) =>
                                                            d.designation === item.designation
                                                                ? { ...d, rate, totalAmount: total }
                                                                : d
                                                        );
                                                        setDesignationSummary(updated);
                                                    }}
                                                    placeholder="Enter rate"
                                                />
                                            </td>
                                            <td>
                                                {item.totalAmount
                                                    ? item.totalAmount
                                                        .toLocaleString("en-IN", { maximumFractionDigits: 0 })
                                                    : "-"}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>

                            {/* ✅ Footer calculations */}
                            <tfoot>
                                {(() => {
                                    const subTotal = designationSummary.reduce(
                                        (sum, item) => sum + (item.totalAmount || 0),
                                        0
                                    );
                                    const cgst = subTotal * 0.09;
                                    const sgst = subTotal * 0.09;
                                    const grandTotal = subTotal + cgst + sgst;

                                    return (
                                        <>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    Sub Total
                                                </th>
                                                <th>{subTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</th>


                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    CGST @ 9%
                                                </th>
                                                <th>{cgst.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>
                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    SGST @ 9%
                                                </th>
                                                <th>{sgst.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>

                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    TOTAL
                                                </th>
                                                <th>{grandTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</th>
                                            </tr>
                                        </>
                                    );
                                })()}
                            </tfoot>

                        </table>
                    </div>
                )}

            </div>




        </div>
    );
};

export default AnnexureEntry;